import { VFC, useMemo } from 'react';
import { isNil } from 'rambda';
import { FormattedMessage, useIntl } from 'react-intl';
import { Commodity, FlowrateQ3, FlowrateQn, FlowrateQp } from '@enmon/commons';
import { Select } from '../Select';
import { MeterDetails as messages } from '../../Form/meter_details/messages';
import { useMeterModelSuggestionsStore } from '../../../store/useMeterModelSuggestionsStore';
import { getSelectOptionsWithSuggestions } from '../utils/getSelectOptionsWithSuggestions';

type FlowrateType = typeof FlowrateQ3 | typeof FlowrateQn | typeof FlowrateQp;

const getEnumValues = (lengths: FlowrateType): number[] => {
  return Object.values(lengths)
    .filter(length => typeof length === 'number')
    .map((length: number) => length);
};

export const getFlowrateValues = (unit?: string | null) => {
  if (!unit) return [];

  switch (unit) {
    case 'Q3':
      return getEnumValues(FlowrateQ3);

    case 'Qn':
      return getEnumValues(FlowrateQn);

    case 'Qp':
      return getEnumValues(FlowrateQp);

    default:
      console.error('Received unknown unit: ', unit);
      return [];
  }
};

interface FlowrateUnitSelectProps {
  commodity: Commodity;
  value?: string;
  onChange?: (value: string) => void;
}

export const FlowrateUnitSelect: VFC<FlowrateUnitSelectProps> = ({ commodity, ...restProps }) => {
  const intl = useIntl();
  const { meterModelSuggestions } = useMeterModelSuggestionsStore();

  const suggestedSelection = meterModelSuggestions?.flowrate?.selection as number[];
  const hasSuggestedSelection = suggestedSelection && suggestedSelection.length > 0;

  const flowrateUnitOptions = useMemo(() => {
    const flowrateUnits = commodity === Commodity.Water ? ['Q3', 'Qn'] : ['Q3', 'Qp'];

    return getSelectOptionsWithSuggestions({
      intl,
      values: flowrateUnits,
      suggestedValue: hasSuggestedSelection ? 'Q3' : null,
    });
  }, [intl, hasSuggestedSelection, commodity]);

  return <Select showArrow className="w-100" options={flowrateUnitOptions} {...restProps} />;
};

interface FlowrateSelectProps {
  value?: number;
  onChange?: (value: number) => void;
  unit?: string | null;
}

export const FlowrateSelect: VFC<FlowrateSelectProps> = ({ unit, ...restProps }) => {
  const intl = useIntl();
  const { meterModelSuggestions } = useMeterModelSuggestionsStore();

  const flowrateOptions = useMemo(() => {
    return getSelectOptionsWithSuggestions({
      intl,
      values: getFlowrateValues(unit),

      // We only suggest flowrate for Q3, as it is the only unit that has a predefined suggestions
      ...(unit === 'Q3' && {
        suggestedValue: meterModelSuggestions?.flowrate?.value,
        suggestedSelection: meterModelSuggestions?.flowrate?.selection,
      }),
    });
  }, [intl, meterModelSuggestions, unit]);

  return (
    <Select
      allowClear
      showArrow
      className="w-100"
      disabled={isNil(unit)}
      options={flowrateOptions}
      placeholder={<FormattedMessage {...messages.flowRatePlaceholder} />}
      suffix={`m${String.fromCharCode(179)}/h`}
      {...restProps}
    />
  );
};
